import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FormModalPropsMap } from 'utils/ui/modals.ts'

export const ORG_MODE_STATUS_LOCAL_STORAGE_KEY = 'userInOrgModeCur'

export interface UiState {
  formModalName: string[]
  formModalProps: FormModalPropsMap[keyof FormModalPropsMap]
  isMobileMenuVisible: boolean
  showSideMenuText: boolean
  sideBarWidth: number
  snackbarType: 'success' | 'error' | 'warning' | 'info'
  snackbarMessage: string | null
  inOrgMode: boolean
  isClearingReduxFormState: boolean
}

const initialState: UiState = {
  formModalName: [],
  formModalProps: {} as any,
  isMobileMenuVisible: false,
  showSideMenuText: window.localStorage.getItem('showSideMenuText') === 'true',
  sideBarWidth: 0,
  snackbarType: 'success',
  snackbarMessage: null,
  inOrgMode: false,
  isClearingReduxFormState: false,
}

type ShowFormModalAction<K extends keyof FormModalPropsMap> = {
  formModalName: K
  formModalProps?: FormModalPropsMap[K]
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    showFormModal: <T extends keyof FormModalPropsMap>(state, action: PayloadAction<ShowFormModalAction<T>>) => {
      state.formModalName = [...state.formModalName, action.payload.formModalName]
      state.formModalProps = action.payload.formModalProps || ({} as FormModalPropsMap[T]) // Cast to specific type
    },
    hideFormModal: (state, action?) => {
      const hideAll = action?.payload || action?.payload === undefined
      state.formModalName = hideAll ? initialState.formModalName : state.formModalName.slice(0, -1)
    },
    toggleMobileMenuVisible: state => {
      state.isMobileMenuVisible = !state.isMobileMenuVisible
    },
    setShowSideMenuText: (state, action) => {
      state.showSideMenuText = action.payload
    },
    setSideBarWidth: (state, action) => {
      state.sideBarWidth = action.payload
    },
    setSnackBar: (state, action) => {
      state.snackbarType = action.payload.snackbarType
      state.snackbarMessage = action.payload.snackbarMessage
    },
    toggleOrgMode: (state, action) => {
      state.inOrgMode = !state.inOrgMode
      window.localStorage.setItem(
        ORG_MODE_STATUS_LOCAL_STORAGE_KEY + action.payload.sub,
        JSON.stringify(state.inOrgMode),
      )
    },
    setOrgMode: (state, action) => {
      state.inOrgMode = action.payload.newValue
      window.localStorage.setItem(
        ORG_MODE_STATUS_LOCAL_STORAGE_KEY + action.payload.sub,
        JSON.stringify(state.inOrgMode),
      )
    },
    // Don't call this directly. Use the clearReduxFormState function in `src/utils/helpers.tsx`
    setIsClearingReduxFormState: (state, action) => {
      state.isClearingReduxFormState = action.payload
    },
  },
})

// Action creators are generated for each case reducer function
export const {
  showFormModal,
  hideFormModal,
  toggleMobileMenuVisible,
  setShowSideMenuText,
  setSideBarWidth,
  setSnackBar,
  toggleOrgMode,
  setOrgMode,
  setIsClearingReduxFormState,
} = uiSlice.actions

export default uiSlice.reducer
